import { ColScaffold, RowScaffold, ScaffoldHeaderCellSpans } from "../../../scaffold/interface";
import { EditorDefns } from "../../../utility";
import {
  ArsTableCellData,
  ArsTableCellType,
  ArsTableRow,
  ArsTableScaffoldModel,
} from "../interface";
import { isArsTableHeaderCell, isArsTableHeaderRow } from "../typeguards";

const defaultColumnWidth = 120;
const defaultRowHeight = 40;

/**
 * Builds derived scaffold props and cell data from ArsTable body state.
 * When calculated rows are available, scaffold dimensions are derived from those rows directly.
 *
 * @param bodyData - Current ArsTable body data
 * @param calculatedTableRows - Calculated scaffold rows used as the primary source of scaffold layout
 * @param editorProps - Editor definitions for the table
 * @returns Derived scaffold model with rows, columns, raw cell map, and header spans
 */
export const buildArsTableScaffoldModel = <T>(
  calculatedTableRows: ArsTableRow<T>[] | null,
  editorProps: EditorDefns<T>,
): ArsTableScaffoldModel<T> => {
  // Send blank table is there is nothing to see here
  if (!calculatedTableRows || calculatedTableRows.length === 0) {
    return {
      cellData: {
        blankRow: {
          blankCol: {
            annotationContent: "Drop items here",
            cellType: ArsTableCellType.RowHeader,
            columnId: "blankCol",
            columnIndex: 0,
            dataPath: "",
            disabled: true,
            displayContent: editorProps.textToT("Drop items here"),
            rowId: "blankRow",
          },
        },
      },
      cols: [{ id: "blankCol", colNumber: 1, width: defaultColumnWidth + 80, noResize: true }],
      rows: [
        {
          id: "blankRow",
          height: defaultRowHeight,
          rowGroupNumber: 0,
          rowNumber: 1,
          type: "header",
        },
      ],
      headerColSpans: {},
    };
  }

  // Set up base objects to return
  const cellData: Record<string, Record<string, ArsTableCellData<T>>> = {};
  const cols: ColScaffold<ArsTableCellData<T>>[] = [];
  const rows: RowScaffold<ArsTableCellData<T>>[] = [];
  const headerColSpans: ScaffoldHeaderCellSpans = {};

  // Tranverse the calculated rows to populate the scaffold model. This will respect any header row spans and use the calculated layout as the source of truth for dimensions and cell placement.
  calculatedTableRows
    // TODO: This shouldn't be required - check getArsTableCalculation
    .filter((r) => r.arsTableCells.length > 0)
    .forEach((calculatedRow, rowIndex) => {
      const rowId = calculatedRow.arsTableCells[0].rowId;
      const rowNumber = rowIndex + 1;

      // Add new row on each pass
      const newRowScaffold: RowScaffold<ArsTableCellData<T>> = {
        id: rowId,
        rowGroupNumber: isArsTableHeaderRow(calculatedRow, editorProps.isT)
          ? -1
          : calculatedRow.rowGroup,
        rowNumber,
        type: isArsTableHeaderRow(calculatedRow, editorProps.isT) ? "header" : "body",
        height: calculatedRow.height,
      };
      rows.push(newRowScaffold);

      // Look at each cell
      calculatedRow.arsTableCells.forEach((cell) => {
        const colSpan = "colSpan" in cell && cell.colSpan ? cell.colSpan : 1;
        const rowSpan = "rowSpan" in cell && cell.rowSpan ? cell.rowSpan : 1;

        const colId = cell.columnId;
        if (
          !cols[cell.columnIndex] &&
          isArsTableHeaderCell(cell, editorProps.isT) &&
          colSpan === 1
        ) {
          cols[cell.columnIndex] = {
            id: colId,
            colNumber: cell.columnIndex + 1,
            width: cell.width ?? defaultColumnWidth,
          };
        }

        // Add cells to cellData map on each pass
        if (!cellData[rowId]) {
          cellData[rowId] = {};
        }
        cellData[rowId][colId] = cell;

        // Add span info to headerColSpans
        if (colSpan > 1 || rowSpan > 1) {
          if (!headerColSpans[rowId]) {
            headerColSpans[rowId] = {};
          }
          headerColSpans[rowId][colId] = { colSpan, rowSpan };
        }
      });
    });

  return {
    cellData,
    cols,
    rows,
    headerColSpans,
  };
};
